import { useMemo } from 'react';
import { FrontAppList } from '../../../../@types';
import {
    ColumnDef, ColumnFiltersState, OnChangeFn, flexRender, getCoreRowModel, getFilteredRowModel, useReactTable
} from "@tanstack/react-table";
import { Box, Spinner, Text, useColorMode } from "theme-ui";
import { TabelClassname } from "../../../utils/Classname";
import TablePagination from "../../Tabels/Pagination";
import FilterPopup from "../../forms-component/filterPopup";
import theme from "../../../theme";
import './frontApp.css';

const getIsOverdue = (item: FrontAppList) => {
    const isDTCompleted = item?.DT === "Completed";
    const isPMCompleted = item?.PM === "Completed";
    if (isDTCompleted || isPMCompleted) return false;

    const itemDateStr = item?.createdAt || item?.created_at;
    if (!itemDateStr) return false;

    let parsedDateMs = new Date(itemDateStr).getTime();
    if (isNaN(parsedDateMs) && typeof itemDateStr === 'string') {
        const parts = itemDateStr.split(/[-/]/);
        if (parts.length === 3) {
            if (parts[0].length === 4) {
                parsedDateMs = new Date(`${parts[0]}-${parts[1]}-${parts[2]}`).getTime();
            } else if (parts[2].length === 4) {
                parsedDateMs = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
            }
        }
    }

    if (isNaN(parsedDateMs)) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const taskDate = new Date(parsedDateMs);
    taskDate.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - taskDate.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 4;
};

interface DataTableProps<T extends FrontAppList> {
    data: T[];
    columns: ColumnDef<T>[];
    className?: string | undefined;
    onPageChange: (page: number | any) => void,
    onPerPageChange: (value: string) => void,
    currentPage?: any,
    TotalCount?: number;
    TotalLength?: any;
    perPageCount: number,
    showNavigation?: boolean;
    enableColumnFilters?: boolean,
    fixedColumn?: boolean;
    onColumnFiltersChange?: OnChangeFn<ColumnFiltersState> | any,
    loader?: boolean;
    highlightCompleted?: boolean;
    rowSelection?: any;
    onRowSelectionChange?: any;
    enableRowSelection?: boolean;
    enableSort?: boolean;
    isPending?: boolean;
    updatingId?: string | number | null;
}
const FrontAppTable = <T extends FrontAppList>({
    data,
    columns,
    className = '',
    onPageChange,
    currentPage,
    TotalCount,
    showNavigation = true,
    TotalLength,
    onPerPageChange,
    perPageCount,
    enableColumnFilters = true,
    onColumnFiltersChange,
    fixedColumn,
    loader = false,
    highlightCompleted = true,
    rowSelection,
    onRowSelectionChange,
    enableRowSelection = false,
    enableSort = true,
    isPending,
    updatingId = null
}: DataTableProps<T>) => {

    const processedData = useMemo(() => {
        if (!data) return [];
        if (!enableSort) return data;
        if (!isPending) return data; // Skip custom sorting for completed view

        return [...data].sort((a: T, b: T) => {
            const getPriority = (item: T) => {
                const isUpdating = item?.comment_id == updatingId;
                if (isUpdating) {
                    return 1;
                }
                if (item?.PM === "Completed") return 3;
                if (item?.DT === "Completed") return 2;
                if (getIsOverdue(item)) return 0;
                return 1;
            };

            const aPriority = getPriority(a);
            const bPriority = getPriority(b);

            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            // Secondary sort: Based on DT completion time, falling back to creation date for stable positioning
            const aTime = new Date(a?.DTUpdatedAt || a?.createdAt || a?.created_at || 0).getTime();
            const bTime = new Date(b?.DTUpdatedAt || b?.createdAt || b?.created_at || 0).getTime();
            return aTime - bTime;
        });
    }, [data, isPending, updatingId]);

    const table = useReactTable({
        data: processedData,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        enableColumnFilters: enableColumnFilters,
        pageCount: TotalLength / perPageCount,
        manualPagination: true,
        getRowId: (row: any) => row?.comment_id,
        enableFilters: true,
        enableGlobalFilter: true,
        getFilteredRowModel: getFilteredRowModel(),
        onColumnFiltersChange: onColumnFiltersChange,
        enableRowSelection,
        onRowSelectionChange,
        state: {
            rowSelection,
        }
    });
    const commonPadding = localStorage.getItem("tPadding") || '13px';
    const [colorMode] = useColorMode();
    const thBackground = colorMode === 'light' ? '#f7efe5' : '#374358';
    const thColor = colorMode === 'light' ? 'black' : '#ffffff';


    return (
        <div className="table-responsive">
            <Box as="div" sx={{ paddingLeft: '15px', paddingRight: '15px', position: "relative" }}>
                <div className={`${fixedColumn ? "tableNotFixHead" : "tableFixHead"} frontAppFixLayout`} style={{ borderRadius: 10, border: `1px solid ${theme.colors?.muted}` }}>
                    <table className={`${className + TabelClassname?.PrimaryTabel} mb-0`}>
                        <thead className="position-relative">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header, index) => {
                                        const isCommentDateColumn = header.column.columnDef.header === "Comment Date";
                                        const isCheckColumn = header.column.columnDef.header === "DT" || header.column.columnDef.header === "PM";
                                        const isConversationColumn = header.column.columnDef.header === "Conversation";
                                        return (
                                            <th className={`th align-middle border-top-0 border-bottom-0 custom-cs-th fixed-column-${index} ${isCommentDateColumn ? 'th-commentDateColumn' : ''} ${isCheckColumn ? 'th-isCheckColumn-frontapp' : ''} ${isConversationColumn ? 'th-isConversationColumn-frontapp' : ''} ${isCommentDateColumn ? 'th-commentDateColumn' : ''}`}
                                                key={index}
                                                style={{ backgroundColor: thBackground, color: thColor, padding: `${commonPadding}px`, textAlign: 'center', }}>
                                                {header.column.getCanFilter() &&
                                                    <FilterPopup column={header.column.columnDef} id={`column${index}`} />}
                                                <Text variant='Poppins16Normal25lh' as={"span"} className="text-center">
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </Text>
                                            </th>
                                        )
                                    })}
                                </tr>
                            ))}

                        </thead>
                        <tbody>
                            {
                                table.getRowModel().rows.map((row: any, rowIndex) => {

                                    let highlightClass = "";
                                    if (highlightCompleted) {
                                        const isUpdating = row?.original?.comment_id == updatingId;
                                        const isDTCompleted = row?.original?.DT === "Completed" && !isUpdating;
                                        const isPMCompleted = row?.original?.PM === "Completed" && !isUpdating;
                                        const isOverdue = getIsOverdue(row?.original);

                                        if (isPMCompleted) {
                                            highlightClass = "";
                                        } else if (isDTCompleted) {
                                            highlightClass = "DTcomplete";
                                        } else if (isOverdue) {
                                            highlightClass = "PMcomplete";
                                        }
                                    }

                                    return (
                                        <tr key={row.id} data-state={row.getIsSelected() && "selected"}
                                            className={highlightClass}
                                            style={{
                                                backgroundColor: colorMode === "light" ? "#fff" : "#20283c",
                                                overflow: 'hidden',
                                                borderBottom:
                                                    (rowIndex + 1) === table.getRowModel().rows?.length ? 'none' :
                                                        '1px solid #D3D5D9',
                                            }}
                                        >
                                            {row.getVisibleCells().map((cell: any, cellIndex: any) => (
                                                <td className={`front-row-${cellIndex}`} key={cell.id} style={{
                                                    padding: `${commonPadding}px`, display: "gird",
                                                    placeContent: "center",
                                                    backgroundColor: colorMode === "light" ? "#fff" : "#20283c",
                                                    color: colorMode === "light" ? "var(--frontapp-table-color, #4a4a4a)" : "var(--frontapp-table-color-dark, #f7efe5)"
                                                }}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            }
                        </tbody>
                    </table>
                    {!loader && data?.length === 0 && (
                        <Box sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 5
                        }}>
                            <Text sx={{ color: thColor }}>No Data Found!</Text>
                        </Box>
                    )}
                </div>
                {loader && (
                    <Box sx={{
                        position: "absolute",
                        top: 0,
                        left: "15px",
                        right: "15px",
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: colorMode === "light" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
                        zIndex: 10,
                        borderRadius: 10
                    }}>
                        <Spinner />
                    </Box>
                )}
            </Box>
            {showNavigation ?
                <Box className="sticky-navigation">
                    <TablePagination
                        onPageChange={(page) => { onPageChange(page) }}
                        onPerPageChange={onPerPageChange}
                        TotalCount={TotalCount}
                        perPageCount={perPageCount}
                        TotalLength={TotalLength}
                        CurrentPage={currentPage}

                    /></Box> : null}
        </div>
    );
};

export default FrontAppTable;
